import React, { useState, useEffect, useContext, useCallback } from 'react';
import DataContext from '../dataContext';
import Spinner from '../Components/Spinner';
import ChartsBar from '../Components/Cards/DataVisualization/ChartsBar';
import ChartsLine from '../Components/Cards/DataVisualization/ChartsLine';
import '../chartConfig';
import '../assets/scss/statistics.scss';

export default function Statistics() {
    const { apiReducer, loading } = useContext(DataContext);
    const [statistics, setStatistics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [topProperties, setTopProperties] = useState(null);
    const [interventionsData, setInterventionsData] = useState(null);
    const [timelineData, setTimelineData] = useState(null);

    const currentYear = new Date().getFullYear();
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
    const [datePreset, setDatePreset] = useState('year');

    const toInputDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();

        switch (preset) {
            case 'year':
                setStartDate(`${year}-01-01`);
                setEndDate(`${year}-12-31`);
                break;
            case 'lastYear':
                setStartDate(`${year - 1}-01-01`);
                setEndDate(`${year - 1}-12-31`);
                break;
            case 'q1':
                setStartDate(`${year}-01-01`);
                setEndDate(`${year}-03-31`);
                break;
            case 'q2':
                setStartDate(`${year}-04-01`);
                setEndDate(`${year}-06-30`);
                break;
            case 'q3':
                setStartDate(`${year}-07-01`);
                setEndDate(`${year}-09-30`);
                break;
            case 'q4':
                setStartDate(`${year}-10-01`);
                setEndDate(`${year}-12-31`);
                break;
            case 'month': {
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                setStartDate(toInputDate(firstDay));
                setEndDate(toInputDate(lastDay));
                break;
            }
            case 'last30': {
                const thirtyDaysAgo = new Date(now);
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                setStartDate(toInputDate(thirtyDaysAgo));
                setEndDate(toInputDate(now));
                break;
            }
            case 'last90': {
                const ninetyDaysAgo = new Date(now);
                ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                setStartDate(toInputDate(ninetyDaysAgo));
                setEndDate(toInputDate(now));
                break;
            }
            case 'custom':
            default:
                break;
        }
    };

    const fetchStatistics = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const dateParams = { startDate, endDate };
            const [dashboardResponse, topPropsResponse, interventionsResponse, timelineResponse] = await Promise.all([
                apiReducer('getStatistics', {}, { type: 'dashboard', ...dateParams }),
                apiReducer('getStatistics', {}, { type: 'top-properties', ...dateParams }),
                apiReducer('getStatistics', {}, { type: 'interventions', ...dateParams }),
                apiReducer('getStatistics', {}, { type: 'timeline' })
            ]);

            if (dashboardResponse.success) {
                setStatistics(dashboardResponse.statistics);
            } else {
                setError('Erreur lors du chargement des statistiques');
            }

            if (topPropsResponse.success) setTopProperties(topPropsResponse.data);
            if (interventionsResponse.success) setInterventionsData(interventionsResponse.data);
            if (timelineResponse.success) setTimelineData(timelineResponse.data);
        } catch (err) {
            console.error('Error fetching statistics:', err);
            setError('Erreur lors du chargement des statistiques');
        } finally {
            setIsLoading(false);
        }
    }, [apiReducer, startDate, endDate]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const formatNumber = (number) => new Intl.NumberFormat('fr-FR').format(number || 0);

    const formatDateRange = () => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const chartColors = {
        blue: 'rgba(0, 57, 82, 0.8)',
        teal: 'rgba(75, 192, 192, 0.75)',
        orange: 'rgba(255, 159, 64, 0.75)',
        red: 'rgba(255, 99, 132, 0.75)'
    };

    const truncateLabel = (label, max = 26) =>
        `${String(label || 'Sans nom').substring(0, max)}${String(label || '').length > max ? '...' : ''}`;

    const prepareTopPropertiesBookingsChart = () => {
        if (!topProperties?.topByBookings?.length) return null;
        return {
            labels: topProperties.topByBookings.map(prop => truncateLabel(prop.propertyName)),
            datasets: [{
                label: 'Réservations',
                data: topProperties.topByBookings.map(prop => prop.bookingsCount),
                backgroundColor: chartColors.blue,
                borderColor: 'rgba(0, 57, 82, 1)',
                borderWidth: 1
            }]
        };
    };

    const prepareTopPropertiesNightsChart = () => {
        if (!topProperties?.topByNights?.length) return null;
        return {
            labels: topProperties.topByNights.map(prop => truncateLabel(prop.propertyName)),
            datasets: [{
                label: 'Nuits',
                data: topProperties.topByNights.map(prop => prop.totalNights),
                backgroundColor: chartColors.orange,
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1
            }]
        };
    };

    const prepareInterventionsChart = () => {
        if (!interventionsData?.topProperties?.length) return null;
        return {
            labels: interventionsData.topProperties.map(prop => truncateLabel(prop.propertyName)),
            datasets: [{
                label: 'Interventions',
                data: interventionsData.topProperties.map(prop => prop.interventionsCount),
                backgroundColor: chartColors.red,
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1
            }]
        };
    };

    const preparePlatformChart = () => {
        if (!statistics?.platformBreakdown?.length) return null;
        return {
            labels: statistics.platformBreakdown.map(item => item.platform),
            datasets: [{
                label: 'Réservations',
                data: statistics.platformBreakdown.map(item => item.count),
                backgroundColor: chartColors.teal,
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
            }]
        };
    };

    const prepareMonthlyActivityChart = () => {
        if (!statistics?.monthlyActivity?.length) return null;
        return {
            labels: statistics.monthlyActivity.map(month => month.monthName),
            datasets: [
                {
                    label: 'Réservations',
                    data: statistics.monthlyActivity.map(month => month.bookings),
                    borderColor: 'rgba(0, 57, 82, 1)',
                    backgroundColor: 'rgba(0, 57, 82, 0.15)',
                    tension: 0.25,
                    fill: false
                },
                {
                    label: 'Nuits',
                    data: statistics.monthlyActivity.map(month => month.nights),
                    borderColor: 'rgba(255, 159, 64, 1)',
                    backgroundColor: 'rgba(255, 159, 64, 0.15)',
                    tension: 0.25,
                    fill: false
                },
                {
                    label: 'Interventions',
                    data: statistics.monthlyActivity.map(month => month.interventions),
                    borderColor: 'rgba(255, 99, 132, 1)',
                    backgroundColor: 'rgba(255, 99, 132, 0.15)',
                    tension: 0.25,
                    fill: false
                }
            ]
        };
    };

    const parseFrenchStatsDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = String(dateStr).split('/');
        if (parts.length !== 3) return null;
        const [day, month, year] = parts.map(Number);
        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? null : date;
    };

    const prepareTimelineChart = () => {
        if (!timelineData?.timeline?.length) return null;

        const rangeStart = new Date(startDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate);
        rangeEnd.setHours(23, 59, 59, 999);

        const validEntries = timelineData.timeline
            .map(entry => ({ ...entry, parsedDate: parseFrenchStatsDate(entry.date) }))
            .filter(entry => entry.parsedDate && entry.parsedDate >= rangeStart && entry.parsedDate <= rangeEnd)
            .sort((a, b) => a.parsedDate - b.parsedDate);

        if (!validEntries.length) return null;

        return {
            labels: validEntries.map(entry => entry.parsedDate.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
            })),
            datasets: [
                {
                    label: 'Concierges',
                    data: validEntries.map(entry => parseInt(entry.concierges) || 0),
                    borderColor: 'rgba(54, 162, 235, 1)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    tension: 0.1,
                    fill: false
                },
                {
                    label: 'Prestataires',
                    data: validEntries.map(entry => parseInt(entry.providers) || 0),
                    borderColor: 'rgba(255, 99, 132, 1)',
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    tension: 0.1,
                    fill: false
                },
                {
                    label: 'Clients',
                    data: validEntries.map(entry => parseInt(entry.clients) || 0),
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.1,
                    fill: false
                }
            ]
        };
    };

    const renderChartCard = (title, chart, type = 'bar', className = '') => (
        <div className={`statistics-card ${className}`}>
            <div className="statistics-card-header">
                <h2>{title}</h2>
            </div>
            <div className="statistics-chart">
                {chart ? (
                    type === 'line' ? <ChartsLine data={chart} /> : <ChartsBar data={chart} />
                ) : (
                    <div className="statistics-empty">Aucune donnée disponible</div>
                )}
            </div>
        </div>
    );

    const renderMovementList = (title, items) => (
        <div className="statistics-card">
            <div className="statistics-card-header">
                <h2>{title}</h2>
            </div>
            <div className="statistics-list">
                {items?.length ? items.map((item, index) => (
                    <div className="statistics-list-row" key={`${title}-${index}`}>
                        <div>
                            <strong>{item.propertyName}</strong>
                            <span>{item.platform} · {item.clientName || 'Client non renseigné'}</span>
                        </div>
                        <small>{formatDateTime(item.date)}</small>
                    </div>
                )) : (
                    <div className="statistics-empty compact">Aucun mouvement à venir</div>
                )}
            </div>
        </div>
    );

    if (isLoading || loading) {
        return (
            <div className="statistics-parent">
                <div className="statistics-loading">
                    <Spinner strokeWidth={9} width={40} color={"#003952"} />
                    <h2>Chargement des statistiques...</h2>
                </div>
            </div>
        );
    }

    if (error || !statistics) {
        return (
            <div className="statistics-parent">
                <div className="statistics-error">
                    <h1>Statistiques</h1>
                    <div className="alert alert-danger" role="alert">
                        {error || 'Aucune donnée disponible'}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="statistics-parent">
            <div className="statistics-toolbar">
                <div className="statistics-toolbar-title">
                    <h1>Statistiques opérationnelles</h1>
                    <span>{formatDateRange()}</span>
                </div>

                <div className="statistics-filter">
                    <label>Période</label>
                    <select value={datePreset} onChange={(e) => handlePresetChange(e.target.value)}>
                        <option value="year">Année en cours ({currentYear})</option>
                        <option value="lastYear">Année précédente ({currentYear - 1})</option>
                        <option value="q1">T1 - Trimestre 1</option>
                        <option value="q2">T2 - Trimestre 2</option>
                        <option value="q3">T3 - Trimestre 3</option>
                        <option value="q4">T4 - Trimestre 4</option>
                        <option value="month">Mois en cours</option>
                        <option value="last30">30 derniers jours</option>
                        <option value="last90">90 derniers jours</option>
                        <option value="custom">Personnalisé</option>
                    </select>
                </div>

                <div className="statistics-filter">
                    <label>Début</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setDatePreset('custom');
                        }}
                    />
                </div>

                <div className="statistics-filter">
                    <label>Fin</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setDatePreset('custom');
                        }}
                    />
                </div>

                <div className="statistics-refresh">
                    <div className="planning-refresh-meta">
                        <span>Dernière mise à jour</span>
                        <span>{new Date(statistics.lastUpdated).toLocaleString('fr-FR')}</span>
                    </div>
                    <button className="planning-refresh-button" onClick={fetchStatistics} disabled={isLoading}>
                        {isLoading ? 'Actualisation...' : 'Actualiser'}
                    </button>
                </div>
            </div>

            <div className="statistics-content">
                <div className="statistics-kpis">
                    <div className="statistics-kpi-card">
                        <span>Réservations</span>
                        <strong>{formatNumber(statistics.reservationsCount)}</strong>
                    </div>
                    <div className="statistics-kpi-card">
                        <span>Nuits réservées</span>
                        <strong>{formatNumber(statistics.nombreNuitsReservees)}</strong>
                        <small>{(statistics.moyenneNuitsParReservation || 0).toFixed(1)} nuits / réservation</small>
                    </div>
                    <div className="statistics-kpi-card">
                        <span>Propriétés actives</span>
                        <strong>{formatNumber(statistics.activeProperties)}</strong>
                        <small>{formatNumber(statistics.activeClients)} clients actifs</small>
                    </div>
                    <div className="statistics-kpi-card">
                        <span>Interventions</span>
                        <strong>{formatNumber(statistics.interventionsCount)}</strong>
                    </div>
                </div>

                <div className="statistics-grid two-columns">
                    {renderChartCard('Réservations, nuits & interventions par mois', prepareMonthlyActivityChart(), 'line', 'wide')}
                    {renderChartCard('Répartition par plateforme', preparePlatformChart())}
                </div>

                <div className="statistics-grid two-columns">
                    {renderMovementList('Arrivées à venir', statistics.upcomingArrivals)}
                    {renderMovementList('Départs à venir', statistics.upcomingDepartures)}
                </div>

                <div className="statistics-grid three-columns">
                    {renderChartCard('Top 10 propriétés - Réservations', prepareTopPropertiesBookingsChart())}
                    {renderChartCard('Top 10 propriétés - Nuits', prepareTopPropertiesNightsChart())}
                    {renderChartCard('Top 10 propriétés - Interventions', prepareInterventionsChart())}
                </div>

                <div className="statistics-grid one-column">
                    {renderChartCard('Évolution concierges, prestataires & clients', prepareTimelineChart(), 'line', 'wide')}
                </div>
            </div>
        </div>
    );
}
